import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi, getWorkspaceId } from '@/lib/api/client';
import { onboardingKeys } from '@/hooks/api/query-keys';
import type { SecondFactorChallenge } from '@/lib/auth/second-factor-handoff';

// ============ Types ============

export interface OnboardingStatus {
  completed: boolean;
  currentStep?: string;
  companyId?: string;
  workspaceId?: string;
  domainId?: string;
  syncProvider?: string;
  syncStartedAt?: string;
  totalUsers?: number;
  syncedUsers?: number;
  totalGroups?: number;
  syncedGroups?: number;
  metadata?: Record<string, unknown>;
}

export interface CreateCompanyDto {
  name: string;
  workspaceName: string;
  industry?: string;
  companySize?: string;
  country?: string;
  logoUrl?: string;
  /** IANA timezone (browser-detected default) — persisted to the workspace so
   *  campaign scheduling defaults to the tenant's zone instead of UTC. */
  timezone?: string;
}

export interface CompanyCreatedResponse {
  companyId: string;
  workspaceId: string;
  slug: string;
  status: OnboardingStatus;
}

export interface CompleteStepDto {
  completed?: boolean;
  skipped?: boolean;
}

export interface CompleteStepResponse {
  success: boolean;
  status: OnboardingStatus;
}

export interface UpdateContextDto {
  currentStep?: string;
  companyId?: string;
  workspaceId?: string;
  domainId?: string;
  syncProvider?: string;
  totalUsers?: number;
  syncedUsers?: number;
  totalGroups?: number;
  syncedGroups?: number;
  metadata?: Record<string, unknown>;
}

export interface InviteTeamMemberDto {
  email: string;
  firstName?: string;
  lastName?: string;
  role?: 'Company Admin' | 'Workspace Admin' | 'Workspace Editor' | 'Workspace Viewer';
  workspaceId: string;
}

export interface ChecklistItem {
  key: string;
  label: string;
  completed: boolean;
  actionUrl?: string;
}

export interface ChecklistResponse {
  items: ChecklistItem[];
  completionPercentage: number;
}

/** A GET /workspaces/my entry, as far as Workspace Setup reads it. */
export interface OnboardingPrefillWorkspace {
  id: string;
  name: string;
  /** The zone it runs on: its own, else its company's, else the platform default. */
  timezone?: string | null;
  /** The interface language it stores ('en', 'ar', 'fr' or 'hi'). */
  locale?: string | null;
}

/**
 * The workspace Workspace Setup shows, and so the one its Next writes (the
 * page names it in the context update): the onboarding context's workspace,
 * else the active one (the switcher's, `getWorkspaceId()`), else the first
 * membership. A workspace the caller does not hold is skipped, so the page
 * never shows one it cannot read.
 */
export function pickOnboardingWorkspace<W extends { id: string }>(
  memberships: readonly W[],
  contextWorkspaceId: string | null | undefined,
  activeWorkspaceId: string | null | undefined,
): W | null {
  const held = (id: string | null | undefined) =>
    id ? memberships.find((w) => w.id === id) : undefined;
  return held(contextWorkspaceId) ?? held(activeWorkspaceId) ?? memberships[0] ?? null;
}

// ============ Query Hooks ============

export function useOnboardingStatus() {
  return useQuery({
    queryKey: onboardingKeys.status(),
    queryFn: () => fetchApi<OnboardingStatus>('/onboarding/status'),
  });
}

export function useOnboardingChecklist() {
  return useQuery({
    queryKey: onboardingKeys.checklist(),
    queryFn: () => fetchApi<ChecklistResponse>('/onboarding/checklist'),
  });
}

export function useOnboardingPrefill() {
  // Only fetch when user is authenticated (re-onboarding from Settings).
  // During pre-auth onboarding (new user), there's no token — skip to avoid 401 → force-logout.
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('hf-app:access-token')
  return useQuery({
    queryKey: onboardingKeys.prefill(),
    enabled: hasToken,
    queryFn: async () => {
      const [profile, workspaces, status, msConfig, googleConfig, team] = await Promise.all([
        fetchApi<{
          name: string; industry?: string; companySize?: string;
          country?: string; logoUrl?: string; timezone?: string;
        }>('/companies/profile').catch(() => null),
        fetchApi<OnboardingPrefillWorkspace[]>('/workspaces/my').catch(() => null),
        // The onboarding context names the workspace the write targets.
        fetchApi<Pick<OnboardingStatus, 'workspaceId'>>('/onboarding/status').catch(() => null),
        fetchApi<{
          isConnected: boolean; integrationType?: string;
          connectionDetails?: { connectedEmail?: string; tenantName?: string };
          syncConfig?: { selectedGroups?: string[]; includeAllUsers?: boolean };
          totalSynced?: number;
        }>('/integrations/microsoft-o365').catch(() => null),
        fetchApi<{
          isConnected: boolean; integrationType?: string;
          connectionDetails?: { connectedEmail?: string };
          syncConfig?: { selectedGroups?: string[]; includeAllUsers?: boolean };
          totalSynced?: number;
        }>('/integrations/google-workspace').catch(() => null),
        fetchApi<{
          data: Array<{ id: string; email: string; firstName?: string; lastName?: string; role: string }>;
        }>('/team').catch(() => null),
      ])

      // Pick whichever provider is connected
      const integrationConfig = msConfig?.isConnected ? msConfig
        : googleConfig?.isConnected ? googleConfig
        : msConfig ?? googleConfig

      const connectedProvider = msConfig?.isConnected ? 'microsoft-o365'
        : googleConfig?.isConnected ? 'google-workspace'
        : null

      // GET /workspaces/my is unordered: its first entry was shown while the
      // write went to the onboarding context's workspace, so a re-run could
      // show workspace B's zone and language and write them to A.
      //
      // The active workspace is the one API calls carry (x-workspace-id),
      // which follows the switcher. The auth store's workspaceId is the one
      // chosen at login: TeamSwitcher never writes it, so after a switch it
      // named a workspace the admin had left.
      const shownWorkspace = pickOnboardingWorkspace(
        Array.isArray(workspaces) ? workspaces : [],
        status?.workspaceId,
        getWorkspaceId(),
      )

      return {
        profile,
        workspace: shownWorkspace,
        integrationConfig,
        connectedProvider,
        teamMembers: team?.data ?? [],
      }
    },
    staleTime: 0,
  })
}

// ============ Mutation Hooks ============

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCompanyDto) =>
      fetchApi<CompanyCreatedResponse>('/onboarding/company', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export function useCompleteStep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stepName, data }: { stepName: string; data: CompleteStepDto }) =>
      fetchApi<CompleteStepResponse>(`/onboarding/steps/${stepName}/complete`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.status() });
    },
  });
}

export function useUpdateOnboardingContext() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateContextDto) =>
      fetchApi<{ success: boolean }>('/onboarding/context', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.status() });
    },
  });
}

export function useCompleteAllSteps() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchApi<{ success: boolean; message: string }>('/onboarding/complete-all', {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export interface OAuthBootstrapDto {
  preAuthToken: string;
  companyName: string;
  workspaceName: string;
  industry?: string;
  companySize?: string;
  country?: string;
}

export interface OAuthBootstrapResponse {
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    role: string;
    mfaEnabled: boolean;
  };
  accessToken: string;
  refreshToken: string;
  companies: Array<{
    id: string;
    name: string;
    role: string;
    workspaces: Array<{
      id: string;
      name: string;
      role: string;
      domains: Array<{ id: string; name: string }>;
    }>;
  }>;
  onboarding: {
    complete: boolean;
    currentStep: string | null;
  };
}

/**
 * RC-E: `/auth/oauth/bootstrap` can answer a second-factor challenge instead of
 * a session (its existing-user branch, reached by a raced or replayed pre-auth
 * token). Declaring only the session shape is how the caller came to read
 * `result.companies[0]` off an object that has none — a TypeError `tsc` could
 * never see. The union makes the compiler demand the narrow.
 */
export type OAuthBootstrapResult =
  | OAuthBootstrapResponse
  | SecondFactorChallenge;

export function useOAuthBootstrap() {
  return useMutation({
    mutationFn: (data: OAuthBootstrapDto) =>
      fetchApi<OAuthBootstrapResult>('/auth/oauth/bootstrap', {
        method: 'POST',
        body: JSON.stringify(data),
        skipAuth: true,
      }),
  });
}

export function useInviteTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InviteTeamMemberDto) =>
      fetchApi<{ success: boolean }>('/onboarding/invite', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
    },
  });
}

export function useResetOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchApi<{ success: boolean; message: string }>('/onboarding/reset', {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      document.cookie = 'hf-app:onboarding-complete=; path=/; max-age=0';
    },
  });
}
